/* eslint-disable react-hooks/exhaustive-deps */
import { Fragment, startTransition, useCallback, useMemo, useRef } from "react"
import type { ReactElement, RefObject } from "react"

import { AnimatePresence, motion } from "framer-motion"
import isEmpty from "lodash-es/isEmpty"
import isUndefined from "lodash-es/isUndefined"
import map from "lodash-es/map"
import { DateTime } from "luxon"
import { Link } from "react-router-dom"
import { useIntersection, useUpdate } from "react-use"

import { type TranslationObj } from "$/utils/lang"

import { ArrowIcon } from "@/3514/assets"
import {
    ETypographyAlignment,
    ETypographyColor,
    ETypographyFamily,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    InfoBox,
    type TCoachNavigationStepperBullet as TStepperBullet,
    Typography
} from "@/3514/components"
import {
    CoachingChatEntity as ChatEntity,
    ECoachingChatEntityMode as EChatMode,
    type TCoachingChatConfig as TChatEntityConfig
} from "@/3514/entities/coaching-chat"
import { THEME } from "@/3514/theme"
import type {
    IChapterBaseModel,
    IChapterCoachingMomentComponentModel as IChatComponentModel,
    IUserParticipantModel
} from "@/3514/types"
import { ERoundDirection, mapChapterCoachingMomentSeedQuestionsToChatSeedQuestions as seedsMapper } from "@/3514/utils"
import {
    IUseTranslation,
    type TGetValueFromTranslatedObjectFunction,
    useGetValueFromTranslatedObject,
    useTranslation
} from "@/hooks"
import { type TEmptyCallback } from "@/shared/types/functions"

import classes from "./coach.group-participant.styles"

function ChatItem({
    component,
    chapter,
    user
}: {
    component: IChatComponentModel
    chapter: IChapterBaseModel
    user: IUserParticipantModel
}): ReactElement {
    const triggerUpdate: TEmptyCallback = useUpdate()

    const containerRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)

    const hasBeenVisibleRef: RefObject<boolean> = useRef<boolean>(false)

    const completionDateRef: RefObject<string> = useRef<string>(null)
    const isReadyRef: RefObject<boolean> = useRef<boolean>(false)

    const intersection: IntersectionObserverEntry | null = useIntersection(containerRef, {
        root: null,
        rootMargin: "50px 0px",
        threshold: 0
    })

    if (intersection?.isIntersecting) hasBeenVisibleRef.current = true // dont wrap in useEffect here!

    const getValueFromTranslatedObject: TGetValueFromTranslatedObjectFunction = useGetValueFromTranslatedObject()

    const chatTitle: string = getValueFromTranslatedObject(component.title)

    const onReady: TEmptyCallback = useCallback(
        (): void =>
            startTransition((): void => {
                isReadyRef.current = true
                triggerUpdate()
            }),
        []
    )

    const onChatComplete: (date: string | null) => void = useCallback(
        (date: string | null): void =>
            startTransition((): void => {
                completionDateRef.current = date
                triggerUpdate()
            }),
        []
    )

    const entityConfig: TChatEntityConfig = useMemo(
        (): TChatEntityConfig => ({
            id: chapter.enrollmentId + component.id,
            mode: EChatMode.Light,
            ref: null,
            insights: {
                isEnabled: component.isChatAvailable ? !isUndefined(component.previousChatCompletionDate) : false,
                previousChatCompletionDate: component.previousChatCompletionDate,
                previousChapterId: component.previousChatChapterId,
                summaryCards: component.previousSummaryCards
            },
            seedQuestions: seedsMapper({ answerData: component.answerData, seedQuestions: component.seedQuestions }),
            availableSummaryCards: component.actualSummaryCards,
            isChatAvailable: component.isChatAvailable,
            isChatCompleted: component.isChatCompleted,
            conversationId: component.answerData?.conversationId,
            areSeedQuestionsRequired: component.isRequired,
            chapterId: Number(chapter.id),
            chatTitle,
            onChatComplete,
            onReady,
            user: { photo: user?.photo, firstName: user?.firstName }
        }),
        [chapter.id, chapter.enrollmentId, chatTitle, component, onReady, onChatComplete, user]
    )

    return (
        <motion.div
            ref={containerRef}
            layout="preserve-aspect"
            initial={{ minHeight: 300 }}
            animate={isReadyRef.current ? { minHeight: 0 } : { minHeight: 300 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className={classes.modulesTab.chatHolder({ isReady: isReadyRef.current })}
        >
            {hasBeenVisibleRef.current ? (
                <Fragment>
                    <div className={classes.modulesTab.chatTitle()}>
                        <Typography
                            text={`${getValueFromTranslatedObject(chapter.title)} Chat`}
                            size={ETypographySize.Medium}
                            color={ETypographyColor.Blue200}
                            family={ETypographyFamily.DmSerif}
                            weight={ETypographyWeight.Medium}
                        />
                        {!!completionDateRef.current && (
                            <Typography
                                text={((): string => {
                                    if (!DateTime.fromISO(completionDateRef.current).isValid)
                                        return "Invalid Date format"

                                    return DateTime.fromISO(completionDateRef.current).toFormat("EEE. dd, HH:mm ZZZZ")
                                })()}
                                color={ETypographyColor.Dark}
                                size={ETypographySize.Tiny}
                            />
                        )}
                    </div>

                    <ChatEntity
                        key={`${chapter.phaseProgressId}-${component.id}-${user?.firstName}`}
                        config={entityConfig}
                        wrapperClassName={classes.modulesTab.chatWrapper()}
                    />
                </Fragment>
            ) : null}
        </motion.div>
    )
}

type _TModulePhaseGroup = {
    key: {
        moduleTitle: TranslationObj
        phaseOrder: number
        phaseName: TranslationObj
        phaseProgressId: number
    }
    chats: { component: IChatComponentModel; chapter: IChapterBaseModel }[]
}
function Module({ group, user }: { group: _TModulePhaseGroup; user: IUserParticipantModel }): ReactElement | null {
    const getValueFromTranslatedObject: TGetValueFromTranslatedObjectFunction = useGetValueFromTranslatedObject()

    return isEmpty(group.chats) ? null : (
        <div className={classes.modulesTab.phaseContainer()}>
            <div className={classes.modulesTab.phaseTitleWrapper()}>
                <Typography
                    text={getValueFromTranslatedObject(group.key.moduleTitle)}
                    tag={ETypographyTag.Heading5}
                    size={ETypographySize.Medium}
                    family={ETypographyFamily.DmSerif}
                    weight={ETypographyWeight.Medium}
                />

                <Typography
                    text={`Phase ${group.key.phaseOrder}: ${getValueFromTranslatedObject(group.key.phaseName)}`}
                    size={ETypographySize.Small}
                    className={classes.modulesTab.phaseValue()}
                />
            </div>

            <div className={classes.modulesTab.chatsWrapper()}>
                {map(
                    group.chats,
                    ({ component, chapter }, chatIndex: number): ReactElement => (
                        <motion.div
                            key={`${chapter.phaseProgressId}-${component.id}`}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "0px 0px -40px 0px" }}
                            transition={{ duration: 0.4, ease: "easeOut", delay: chatIndex * 0.06 }}
                        >
                            <ChatItem component={component} chapter={chapter} user={user} />
                        </motion.div>
                    )
                )}
            </div>
        </div>
    )
}

function Navigation({
    bullets,
    currentBullet,
    isFetching
}: {
    bullets: TStepperBullet[]
    currentBullet: TStepperBullet | undefined
    isFetching: boolean
}): ReactElement {
    const currentIndex: number = bullets.findIndex((b: TStepperBullet): boolean => b.pageId === currentBullet?.pageId)

    const prevBullet: TStepperBullet | undefined = currentIndex > 0 ? bullets[currentIndex - 1] : undefined
    const nextBullet: TStepperBullet | undefined =
        currentIndex !== -1 && currentIndex < bullets.length - 1 ? bullets[currentIndex + 1] : undefined

    const isVisible: boolean = !isFetching && !!currentBullet && !isEmpty(bullets) && (!!prevBullet || !!nextBullet)

    return (
        <AnimatePresence initial={false}>
            {isVisible && (
                <motion.div
                    key="module-nav"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    className={classes.modulesTab.navigationContainer()}
                >
                    {prevBullet?.href ? (
                        <Link to={prevBullet.href} className={classes.modulesTab.navigationWrapper()}>
                            <motion.span
                                whileFocus={{ scale: 1.075 }}
                                whileHover={{ scale: 1.075 }}
                                transition={{ duration: 0.2, ease: "easeIn" }}
                                className={classes.modulesTab.navigationArrow()}
                            >
                                <ArrowIcon direction={ERoundDirection.Left} fillColor={THEME.COLORS.blue[200]} />
                            </motion.span>
                            <div className={classes.modulesTab.navigationText()}>
                                <Typography
                                    text={prevBullet.title}
                                    weight={ETypographyWeight.Medium}
                                    family={ETypographyFamily.DmSerif}
                                    color={ETypographyColor.Dark}
                                />
                                <Typography
                                    text={prevBullet.subTitle}
                                    color={ETypographyColor.Gray23}
                                    size={ETypographySize.Tiny}
                                />
                            </div>
                        </Link>
                    ) : (
                        <div className={classes.modulesTab.navigationWrapper()} />
                    )}

                    {nextBullet?.href ? (
                        <Link to={nextBullet.href} className={classes.modulesTab.navigationWrapper()}>
                            <div className={classes.modulesTab.navigationText()}>
                                <Typography
                                    text={nextBullet.title}
                                    weight={ETypographyWeight.Medium}
                                    family={ETypographyFamily.DmSerif}
                                    color={ETypographyColor.Dark}
                                />
                                <Typography
                                    text={nextBullet.subTitle}
                                    color={ETypographyColor.Gray23}
                                    size={ETypographySize.Tiny}
                                />
                            </div>
                            <motion.span
                                whileFocus={{ scale: 1.075 }}
                                whileHover={{ scale: 1.075 }}
                                transition={{ duration: 0.2, ease: "easeIn" }}
                                className={classes.modulesTab.navigationArrow()}
                            >
                                <ArrowIcon direction={ERoundDirection.Right} fillColor={THEME.COLORS.blue[200]} />
                            </motion.span>
                        </Link>
                    ) : (
                        <div className={classes.modulesTab.navigationWrapper()} />
                    )}
                </motion.div>
            )}
        </AnimatePresence>
    )
}

function EmptyParam({ isVisible }: { isVisible: boolean }): ReactElement {
    const { t }: IUseTranslation = useTranslation()

    return (
        isVisible && (
            <Typography
                className={classes.modulesTab.emptyHint()}
                text={t("coachSide.groupParticipantPage.modulesTab.emptyParam")}
                color={ETypographyColor.Dark}
                size={ETypographySize.Small}
            />
        )
    )
}

function EmptyBullets({ isVisible }: { isVisible: boolean }): ReactElement {
    const { t }: IUseTranslation = useTranslation()

    return (
        isVisible && (
            <Typography
                className={classes.modulesTab.emptyHint()}
                text={t("coachSide.groupParticipantPage.modulesTab.emptyData")}
                color={ETypographyColor.Dark}
                size={ETypographySize.Small}
            />
        )
    )
}

function QueuedModule({ isVisible, date, name }: { isVisible: boolean; date: string; name: string }): ReactElement {
    const { t }: IUseTranslation = useTranslation()

    return (
        isVisible && (
            <div className={classes.modulesTab.unassignedContainer()}>
                <div>
                    <Typography
                        text={name}
                        color={ETypographyColor.Blue200}
                        tag={ETypographyTag.Heading3}
                        size={ETypographySize.Heading2}
                        weight={ETypographyWeight.Medium}
                        family={ETypographyFamily.DmSerif}
                        alignment={ETypographyAlignment.Center}
                    />
                    <Typography
                        text={t("coachSide.groupParticipantPage.modulesTab.noPhases")}
                        color={ETypographyColor.Blue200}
                        tag={ETypographyTag.Paragraph}
                        size={ETypographySize.Medium}
                        weight={ETypographyWeight.Medium}
                        family={ETypographyFamily.DmSerif}
                        alignment={ETypographyAlignment.Center}
                    />
                </div>

                <InfoBox
                    variant="info"
                    title={t("coachSide.groupParticipantPage.modulesTab.warnings.notOpen.title")}
                    message={t("coachSide.groupParticipantPage.modulesTab.warnings.notOpen.description", {
                        date: DateTime.fromISO(date).toFormat("LLL dd")
                    })}
                />
            </div>
        )
    )
}

function UnassignedModuleFuture({ isVisible }: { isVisible: boolean }): ReactElement {
    const { t }: IUseTranslation = useTranslation()

    return (
        isVisible && (
            <div className={classes.modulesTab.unassignedContainer()}>
                <div>
                    <Typography
                        text={t("common.unassigned")}
                        color={ETypographyColor.Blue200}
                        tag={ETypographyTag.Heading3}
                        size={ETypographySize.Heading2}
                        weight={ETypographyWeight.Medium}
                        family={ETypographyFamily.DmSerif}
                        alignment={ETypographyAlignment.Center}
                    />
                    <Typography
                        text={t("coachSide.groupParticipantPage.modulesTab.noPhases")}
                        color={ETypographyColor.Blue200}
                        tag={ETypographyTag.Paragraph}
                        size={ETypographySize.Medium}
                        weight={ETypographyWeight.Medium}
                        family={ETypographyFamily.DmSerif}
                        alignment={ETypographyAlignment.Center}
                    />
                </div>

                <InfoBox
                    variant="info"
                    title={t("coachSide.groupParticipantPage.modulesTab.warnings.notAvailable.title")}
                    message={t("coachSide.groupParticipantPage.modulesTab.warnings.notAvailable.description")}
                />
            </div>
        )
    )
}

function UnassignedModulePast({ isVisible }: { isVisible: boolean }): ReactElement {
    const { t }: IUseTranslation = useTranslation()

    return (
        isVisible && (
            <div className={classes.modulesTab.unassignedContainer()}>
                <div>
                    <Typography
                        text={t("common.unassigned")}
                        color={ETypographyColor.Blue200}
                        tag={ETypographyTag.Heading3}
                        size={ETypographySize.Heading2}
                        weight={ETypographyWeight.Medium}
                        family={ETypographyFamily.DmSerif}
                        alignment={ETypographyAlignment.Center}
                    />
                    <Typography
                        text={t("coachSide.groupParticipantPage.modulesTab.noPhases")}
                        color={ETypographyColor.Blue200}
                        tag={ETypographyTag.Paragraph}
                        size={ETypographySize.Medium}
                        weight={ETypographyWeight.Medium}
                        family={ETypographyFamily.DmSerif}
                        alignment={ETypographyAlignment.Center}
                    />
                </div>

                <InfoBox
                    variant="warning"
                    title={t("coachSide.groupParticipantPage.modulesTab.warnings.notAssigned.title")}
                    message={t("coachSide.groupParticipantPage.modulesTab.warnings.notAssigned.description")}
                />
            </div>
        )
    )
}

export default {
    modulesTab: {
        Module,
        Navigation,
        EmptyParam,
        EmptyBullets,
        QueuedModule,
        UnassignedModulePast,
        UnassignedModuleFuture
    }
}
